import React from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaMinus, FaTrash, FaArrowLeft, FaShoppingBag } from "react-icons/fa";
import { useCart } from "../contexts/CartContext";
import "../styles/Cart.css";
import { IMAGE_BASE_URL } from "../api/api"; // ✅ centralized import

function Cart() {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, getCartTotal, clearCart } = useCart();

  const handleQuantityChange = (itemId, newQuantity) => {
    updateQuantity(itemId, newQuantity);
  };

  const handleRemoveItem = (itemId) => {
    if (window.confirm("Are you sure you want to remove this item from cart?")) {
      removeFromCart(itemId);
    }
  };

  const handleCheckout = () => {
    navigate("/checkout");
  };

  // Calculate values
  const subtotal = getCartTotal();
  const tax = 0; // Tax is always 0
  const shipping = 0; // Free shipping
  const total = subtotal + tax + shipping; // Total without tax

  // 🛒 Empty Cart State
  if (cart.items.length === 0) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <FaShoppingBag size={64} className="text-muted mb-3" />
          <h3>Your cart is empty</h3>
          <p className="text-muted mb-4">Add some items to your cart to continue shopping.</p>
          <button
            className="btn text-white"
            style={{ backgroundColor: "#FD7E14" }}
            onClick={() => navigate("/")}
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  // 🧾 Main Cart View
  return (
    <div className="container py-4">
      <div className="row">
        {/* 🛍 Cart Items Section */}
        <div className="col-lg-8">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Shopping Cart ({cart.items.length} items)</h2>
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => {
                if (window.confirm("Are you sure you want to clear your cart?")) {
                  clearCart();
                }
              }}
            >
              Clear Cart
            </button>
          </div>

          {/* Cart Items List */}
          <div className="card">
            <div className="card-body">
              {cart.items.map((item) => {
                const imageUrl = item.productImage
                  ? item.productImage.startsWith("http")
                    ? item.productImage
                    : `${IMAGE_BASE_URL}storage/${item.productImage}` // ✅ unified image path
                  : "https://media.istockphoto.com/id/1147544807/vector/thumbnail-image-vector-graphic.jpg?s=612x612&w=0&k=20&c=rnCKVbdxqkjlcs3xH87-9gocETqpspHFXu5dIGB4wuM=";

                return (
                  <div key={item.id} className="row align-items-center border-bottom pb-3 mb-3">
                    {/* Product Image */}
                    <div className="col-md-2">
                      <img
                        src={imageUrl}
                        alt={item.productName}
                        className="img-fluid rounded"
                        style={{ width: "80px", height: "80px", objectFit: "cover" }}
                      />
                    </div>

                    {/* Product Details */}
                    <div className="col-md-4">
                      <h6 className="mb-1">{item.productName}</h6>
                      {item.variantName && (
                        <p className="text-muted mb-1 small">Variant: {item.variantName}</p>
                      )}
                      <p className="text-primary mb-0">Rs.{item.price}.00</p>
                    </div>

                    {/* Quantity Controls */}
                    <div className="col-md-3">
                      <div className="d-flex align-items-center">
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          <FaMinus size={12} />
                        </button>
                        <span className="mx-3">{item.quantity}</span>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                        >
                          <FaPlus size={12} />
                        </button>
                      </div>
                    </div>

                    {/* Subtotal & Remove */}
                    <div className="col-md-3 text-end">
                      <p className="fw-bold mb-1">
                        Rs.{(item.price * item.quantity).toFixed(2)}
                      </p>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleRemoveItem(item.id)}
                      >
                        <FaTrash size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Continue Shopping Button */}
          <div className="mt-3">
            <button className="btn btn-outline-secondary" onClick={() => navigate("/")}>
              <FaArrowLeft className="me-2" />
              Continue Shopping
            </button>
          </div>
        </div>

        {/* 💰 Order Summary */}
        <div className="col-lg-4">
          <div className="card sticky-top" style={{ top: "20px" }}>
            <div className="card-header">
              <h5 className="mb-0">Order Summary</h5>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between mb-2">
                <span>
                  Subtotal (
                  {cart.items.reduce((sum, item) => sum + item.quantity, 0)} items)
                </span>
                <span>Rs.{subtotal.toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Shipping</span>
                <span className="text-success">Free</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Tax</span>
                <span>Rs.{tax.toFixed(2)}</span>
              </div>
              <hr />
              <div className="d-flex justify-content-between mb-3">
                <strong>Total</strong>
                <strong>Rs.{total.toFixed(2)}</strong>
              </div>

              <button
                className="btn w-100 py-2 text-white"
                style={{ backgroundColor: "#FD7E14" }}
                onClick={handleCheckout}
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Cart;